import React, { useState, useRef, useEffect, useCallback } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Box,
  Button,
  Typography,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { Event, Game, PackageGame, GameCondition } from "../types";
import { divertilandiaColors } from "../theme/divertilandiaColors";
import { gameService, packageService } from "../services/api";
import logoImage from "../logo_divertilandia.png";

interface ContractPDFProps {
  event: Event;
}

const ContractPDF: React.FC<ContractPDFProps> = ({ event }) => {
  const [editMode, setEditMode] = useState(false);
  const [packageGames, setPackageGames] = useState<PackageGame[]>([]);
  const [editedPackageGames, setEditedPackageGames] = useState<PackageGame[]>(
    []
  );
  const [allGames, setAllGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const contractRef = useRef<HTMLDivElement>(null);
  const contractPage2Ref = useRef<HTMLDivElement>(null);
  const contractPage3Ref = useRef<HTMLDivElement>(null);
  const contractPage4Ref = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    if (!event.package?.id) return;

    setLoading(true);
    try {
      const [packageData, gamesData] = await Promise.all([
        packageService.getById(event.package.id),
        gameService.getAll(),
      ]);

      // Convertir los juegos del paquete al formato esperado
      const packageGamesData: PackageGame[] =
        packageData.games?.map((game) => ({
          id: game.id,
          name: game.name,
          toy_type: game.toy_type,
          quantity: game.quantity,
          unit_price: game.unit_price,
          condition: game.condition,
          image_base64: game.image_base64,
          is_active: game.is_active,
          toy_clauses: game.toy_clauses || [], // ¡Agregar las cláusulas!
          pivot: {
            quantity: game.pivot?.quantity || 1,
          },
        })) || [];

      setPackageGames(packageGamesData);
      setAllGames(gamesData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [event.package?.id]);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadData();
  }, [event.package?.id, loadData]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  // Función para obtener las cláusulas únicas de los juguetes del paquete (usando los juegos editados)
  const getPackageClauses = () => {
    const clausesSet = new Set<string>();

    // Usar editedPackageGames en lugar de packageGames para reflejar los cambios
    const currentGames =
      editedPackageGames.length > 0 ? editedPackageGames : packageGames;

    currentGames.forEach((game, index) => {
      if (game.toy_clauses && game.toy_clauses.length > 0) {
        game.toy_clauses.forEach((clause) => {
          if (clause.is_active) {
            clausesSet.add(clause.description);
          }
        });
      }
    });

    const finalClauses = Array.from(clausesSet);

    return finalClauses;
  };

  const formatDate = (dateString: string): string => {
    // Crear fecha local sin problemas de zona horaria
    const [year, month, day] = dateString.split("T")[0].split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string): string => {
    if (!timeString) return '';
    // Si el tiempo tiene formato HH:MM:SS, extraer solo HH:MM
    const timeParts = timeString.split(':');
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    return timeString;
  };

  const getPackageColor = (childGender: string | null | undefined): string => {
    if (childGender === 'male') {
      return '#1976d2'; // Azul para niño
    } else if (childGender === 'female') {
      return '#e91e63'; // Rosa para niña
    }
    return divertilandiaColors.standard.primary; // Color por defecto
  };

  const today = new Date().toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Calcular costos
  const packagePrice = Number(event.package?.price || 0);
  const discountAmount =
    packagePrice * (Number(event.discount_percentage || 0) / 100);
  const packagePriceWithDiscount = packagePrice - discountAmount;

  const extraHoursCost = Number(event.extra_hours_count || 0) * 100;
  const extraTablesCost = Number(event.extra_tables || 0) * 100;
  const extraChairsCost = Number(event.extra_chairs || 0) * 50;
  const extraPlaypensCost = Number(event.extra_playpens || 0) * 200;
  const extraToysCost = Number(event.extra_toys || 0) * 100;
  const extraServicesCost = Number(event.extra_services_cost || 0);

  const totalExtras =
    extraHoursCost +
    extraTablesCost +
    extraChairsCost +
    extraPlaypensCost +
    extraToysCost +
    extraServicesCost;
  const totalCost = packagePriceWithDiscount + totalExtras;
  const advancePayment = Number(event.advance_payment_amount || 0);
  const remainingBalance = totalCost - advancePayment;

  const handleEditData = () => {
    // Si entramos en modo edición y no hay juegos editados, inicializar con los originales
    if (!editMode && editedPackageGames.length === 0) {
      setEditedPackageGames([...packageGames]);
    }
    setEditMode(!editMode);
  };

  const handleSaveData = () => {
    // Si no hay juegos editados, inicializar con los juegos originales
    if (editedPackageGames.length === 0) {
      setEditedPackageGames([...packageGames]);
    }
    setEditMode(false);
  };

  const handleQuantityChange = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;

    const currentGames =
      editedPackageGames.length > 0 ? editedPackageGames : packageGames;
    const updatedGames = [...currentGames];
    updatedGames[index] = {
      ...updatedGames[index],
      pivot: {
        ...updatedGames[index].pivot,
        quantity: newQuantity,
      },
    };
    setEditedPackageGames(updatedGames);
  };

  const handleGameChange = (index: number, gameId: string) => {
    const currentGames =
      editedPackageGames.length > 0 ? editedPackageGames : packageGames;
    const selectedGame = allGames.find((game) => game.id === gameId);

    if (!selectedGame) return;

    const updatedGames = [...currentGames];
    updatedGames[index] = {
      ...updatedGames[index],
      id: selectedGame.id,
      name: selectedGame.name,
      toy_type: selectedGame.toy_type,
      unit_price: selectedGame.unit_price,
      condition: selectedGame.condition,
      image_base64: selectedGame.image_base64,
      is_active: selectedGame.is_active,
      toy_clauses: selectedGame.toy_clauses || [], // ¡Incluir las cláusulas del juguete seleccionado!
    };

    setEditedPackageGames(updatedGames);
  };

  const handleRemoveGame = (index: number) => {
    const currentGames =
      editedPackageGames.length > 0 ? editedPackageGames : packageGames;
    const updatedGames = currentGames.filter((_, i) => i !== index);
    setEditedPackageGames(updatedGames);
  };

  const handleAddGame = () => {
    // Si editedPackageGames está vacío, inicializar con los juegos originales
    const currentGames =
      editedPackageGames.length > 0 ? editedPackageGames : packageGames;

    // Agregar un juguete vacío que el usuario puede configurar
    const newGame: PackageGame = {
      id: "",
      name: "Seleccionar juguete",
      toy_type: undefined,
      quantity: 1,
      unit_price: 0,
      condition: GameCondition.EXCELLENT,
      image_base64: "",
      is_active: true,
      toy_clauses: [], // Inicializar con array vacío de cláusulas
      pivot: {
        quantity: 1,
      },
    };

    setEditedPackageGames([...currentGames, newGame]);
  };

  const getGamesByToyType = (
    toyTypeId: string,
    excludeCurrentGame?: string
  ): Game[] => {
    // Si no hay toyTypeId, devolver todos los juegos activos (para elementos nuevos)
    if (!toyTypeId) {
      return allGames.filter((game) => game.is_active);
    }

    return allGames.filter((game) => {
      // Verificar condiciones básicas
      if (game.toy_type_id !== toyTypeId || !game.is_active) return false;

      // Si es el juego actual, siempre incluirlo (para permitir mantener la selección)
      if (game.id === excludeCurrentGame) return true;

      // Verificar disponibilidad: solo incluir si tiene unidades disponibles
      const availableUnits = game.available_count || 0;

      // Contar cuántas unidades de este juego ya están siendo usadas en otros elementos del paquete
      const usedInPackage = packageGames.reduce((count, packageGame) => {
        if (
          packageGame.id === game.id &&
          packageGame.id !== excludeCurrentGame
        ) {
          return count + packageGame.pivot.quantity;
        }
        return count;
      }, 0);

      // Solo incluir si quedan unidades disponibles después de restar las usadas
      return availableUnits > usedInPackage;
    });
  };

  const generatePDF = async () => {
    if (
      !contractRef.current ||
      !contractPage2Ref.current ||
      !contractPage3Ref.current ||
      !contractPage4Ref.current
    )
      return;

    setGeneratingPdf(true);

    try {
      // Detectar si estamos en móvil
      const isMobile = window.innerWidth < 768;
      
      // Guardar estilos originales
      const elements = [
        contractRef.current,
        contractPage2Ref.current,
        contractPage3Ref.current,
        contractPage4Ref.current,
      ];
      
      const originalStyles = elements.map((el) => ({
        width: el.style.width,
        minWidth: el.style.minWidth,
        maxWidth: el.style.maxWidth,
        transform: el.style.transform,
        position: el.style.position,
      }));

      // Forzar ancho fijo A4 (210mm = 793.7px a 96 DPI) para captura correcta
      const a4WidthPx = 793.7;
      elements.forEach((el) => {
        el.style.width = `${a4WidthPx}px`;
        el.style.minWidth = `${a4WidthPx}px`;
        el.style.maxWidth = `${a4WidthPx}px`;
        
        // Si es móvil, temporalmente posicionar fuera de la vista
        if (isMobile) {
          el.style.position = 'absolute';
          el.style.left = '-9999px';
        }
      });

      // Esperar a que el navegador renderice con los nuevos tamaños
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Configuración optimizada de html2canvas
      const canvasOptions = {
        scale: isMobile ? 3 : 2, // Mayor escala en móvil para mejor calidad
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: a4WidthPx,
        windowWidth: a4WidthPx,
      };

      // Página 1: Información principal del contrato
      const canvas1 = await html2canvas(contractRef.current, canvasOptions);

      // Página 2: Detalles del evento
      const canvas2 = await html2canvas(contractPage2Ref.current, canvasOptions);

      // Página 3: Cláusulas del contrato
      const canvas3 = await html2canvas(contractPage3Ref.current, canvasOptions);

      // Página 4: Cláusulas adicionales
      const canvas4 = await html2canvas(contractPage4Ref.current, canvasOptions);

      // Restaurar estilos originales
      elements.forEach((el, index) => {
        el.style.width = originalStyles[index].width;
        el.style.minWidth = originalStyles[index].minWidth;
        el.style.maxWidth = originalStyles[index].maxWidth;
        el.style.transform = originalStyles[index].transform;
        el.style.position = originalStyles[index].position;
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210; // A4 width in mm

      // Agregar página 1
      const imgData1 = canvas1.toDataURL("image/jpeg", 0.95); // JPEG con calidad alta
      const imgHeight1 = (canvas1.height * imgWidth) / canvas1.width;
      pdf.addImage(imgData1, "JPEG", 0, 0, imgWidth, imgHeight1);

      // Agregar página 2
      pdf.addPage();
      const imgData2 = canvas2.toDataURL("image/jpeg", 0.95);
      const imgHeight2 = (canvas2.height * imgWidth) / canvas2.width;
      pdf.addImage(imgData2, "JPEG", 0, 0, imgWidth, imgHeight2);

      // Agregar página 3
      pdf.addPage();
      const imgData3 = canvas3.toDataURL("image/jpeg", 0.95);
      const imgHeight3 = (canvas3.height * imgWidth) / canvas3.width;
      pdf.addImage(imgData3, "JPEG", 0, 0, imgWidth, imgHeight3);

      // Agregar página 4
      pdf.addPage();
      const imgData4 = canvas4.toDataURL("image/jpeg", 0.95);
      const imgHeight4 = (canvas4.height * imgWidth) / canvas4.width;
      pdf.addImage(imgData4, "JPEG", 0, 0, imgWidth, imgHeight4);

      pdf.save(`contrato-${event.contact_name}-${event.id}.pdf`);
    } catch (error) {
      console.error("Error generando PDF:", error);
      alert("Error al generar el PDF. Por favor, intenta de nuevo.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <Box>
      {/* Panel de edición */}
      {editMode && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography
              variant="h6"
              sx={{ color: divertilandiaColors.standard.primary }}
            >
              Personalizar Juguetes del Paquete
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddGame}
              size="small"
              sx={{
                borderColor: divertilandiaColors.standard.primary,
                color: divertilandiaColors.standard.primary,
              }}
            >
              Agregar Juguete
            </Button>
          </Box>

          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ mb: 2, color: "text.secondary" }}
          >
            Puedes modificar cantidades, cambiar juguetes, eliminar elementos o
            agregar extras:
          </Typography>

          {loading ? (
            <Typography>Cargando juguetes...</Typography>
          ) : (
            <Grid container spacing={2}>
              {(editedPackageGames.length > 0
                ? editedPackageGames
                : packageGames
              ).map((game, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    sx={{
                      p: 2,
                      border:
                        game.id === ""
                          ? "2px dashed #ccc"
                          : "1px solid #e0e0e0",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      {/* Control de cantidad */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 140,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleQuantityChange(index, game.pivot.quantity - 1)
                          }
                          disabled={game.pivot.quantity <= 1}
                          sx={{
                            color: divertilandiaColors.standard.primary,
                            "&:disabled": { color: "#ccc" },
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={game.pivot.quantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              index,
                              parseInt(e.target.value) || 1
                            )
                          }
                          sx={{ width: 70 }}
                          inputProps={{
                            min: 1,
                            style: { textAlign: "center" },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleQuantityChange(index, game.pivot.quantity + 1)
                          }
                          sx={{ color: divertilandiaColors.standard.primary }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{ minWidth: 100, color: "text.secondary" }}
                      >
                        {game.toy_type?.name ||
                          (game.id === "" ? "Nuevo elemento" : "Sin tipo")}
                      </Typography>

                      <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {game.id === ""
                              ? "Seleccionar juguete"
                              : "Cambiar juguete"}
                          </InputLabel>
                          <Select
                            value={game.id}
                            label={
                              game.id === ""
                                ? "Seleccionar juguete"
                                : "Cambiar juguete"
                            }
                            onChange={(e) =>
                              handleGameChange(index, e.target.value)
                            }
                          >
                            {getGamesByToyType(
                              game.toy_type?.id || "",
                              game.id
                            ).map((availableGame) => (
                              <MenuItem
                                key={availableGame.id}
                                value={availableGame.id}
                              >
                                {availableGame.name}
                                {availableGame.available_count !==
                                  undefined && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    (Disponibles:{" "}
                                    {availableGame.available_count})
                                  </Typography>
                                )}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {game.image_base64 && (
                        <Box sx={{ width: 50, height: 50, ml: 1 }}>
                          <img
                            src={
                              game.image_base64.startsWith("data:")
                                ? game.image_base64
                                : `data:image/webp;base64,${game.image_base64}`
                            }
                            alt={game.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      )}

                      {/* Botón eliminar */}
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveGame(index)}
                        size="small"
                        sx={{ ml: 1 }}
                        title="Eliminar juguete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    {game.id === "" && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ mt: 1, display: "block" }}
                      >
                        ⚠️ Selecciona un juguete para completar este elemento
                      </Typography>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box mt={3} display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveData}
              sx={{
                backgroundColor: divertilandiaColors.standard.primary,
                "&:hover": {
                  backgroundColor: divertilandiaColors.rainbow.blue,
                },
              }}
            >
              Guardar Cambios
            </Button>

            <Button
              variant="outlined"
              onClick={() => setEditMode(false)}
              sx={{
                borderColor: divertilandiaColors.standard.primary,
                color: divertilandiaColors.standard.primary,
              }}
            >
              Cancelar
            </Button>
          </Box>
        </Paper>
      )}

      {/* Controles */}
      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEditData}
          sx={{
            borderColor: divertilandiaColors.standard.primary,
            color: divertilandiaColors.standard.primary,
            "&:hover": {
              borderColor: divertilandiaColors.rainbow.blue,
              backgroundColor: "rgba(46, 125, 50, 0.04)",
            },
          }}
        >
          {editMode ? "Cancelar Edición" : "Personalizar Juguetes"}
        </Button>

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={generatePDF}
          disabled={generatingPdf}
          sx={{
            backgroundColor: divertilandiaColors.standard.primary,
            "&:hover": {
              backgroundColor: divertilandiaColors.rainbow.blue,
            },
          }}
        >
          {generatingPdf ? "Generando..." : "Descargar PDF"}
        </Button>
      </Box>

      {/* Documento del contrato */}
      <Paper
        ref={contractRef}
        sx={{
          p: 4,
          maxWidth: "210mm",
          margin: "0 auto",
          backgroundColor: "white",
          color: "black",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          mb={3}
          pb={2}
          borderBottom="2px solid #e0e0e0"
        >
          <Box sx={{ mr: 3, width: 80, height: 80 }}>
            <img
              src={logoImage}
              alt="Logo Divertilandia"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
          <Box textAlign="center" flex={1}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 1,
              }}
            >
              Divertilandia ENLU
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Solicitud de Servicio
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fecha: {today}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contrato No. {event.id}
            </Typography>
          </Box>
        </Box>

        {/* Información del cliente */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 2,
              }}
            >
              INFORMACIÓN DEL CLIENTE
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                      Nombre:
                    </TableCell>
                    <TableCell>{event.contact_name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Teléfono Principal:
                    </TableCell>
                    <TableCell>{event.contact_phone}</TableCell>
                  </TableRow>
                  {event.secondary_phone && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Teléfono Alternativo:
                      </TableCell>
                      <TableCell>{event.secondary_phone}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Fecha del Evento:
                    </TableCell>
                    <TableCell>{formatDate(event.event_date)}</TableCell>
                  </TableRow>
                  {event.child_gender && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Género del Festejado:
                      </TableCell>
                      <TableCell sx={{ color: getPackageColor(event.child_gender), fontWeight: "bold" }}>
                        {event.child_gender === "male" ? "Niño" : "Niña"}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Domicilio:
                    </TableCell>
                    <TableCell>{event.address}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Información del paquete */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 2,
              }}
            >
              PAQUETE CONTRATADO
            </Typography>

            {/* Información del paquete centrada */}
            <Box textAlign="center" mb={3}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: "bold", 
                  mb: 1,
                  color: getPackageColor(event.child_gender)
                }}
              >
                {event.package?.name || "Sin paquete"}
              </Typography>
              {event.package?.description && (
                <Typography variant="body1" color="text.secondary">
                  {event.package.description}
                </Typography>
              )}
            </Box>

            <Box display="flex" gap={3}>
              {/* Lista cuadriculada de juguetes a la izquierda */}
              <Box flex={1}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                  Incluye:
                </Typography>

                {(() => {
                  const currentGames =
                    editedPackageGames.length > 0
                      ? editedPackageGames
                      : packageGames;
                  return currentGames.length > 0 ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: "#fafafa",
                        minHeight: 200,
                      }}
                    >
                      {currentGames.map((game, index) => (
                        <React.Fragment key={index}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: "bold",
                              color: divertilandiaColors.standard.primary,
                              textAlign: "center",
                              px: 1,
                            }}
                          >
                            {game.pivot.quantity}
                          </Typography>
                          <Typography variant="body2" sx={{ px: 1 }}>
                            {game.name}
                          </Typography>
                        </React.Fragment>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Cargando juguetes...
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>

              {/* Imagen del paquete a la derecha */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor: "#f9f9f9",
                  p: 2,
                }}
              >
                {event.package?.image_base64 ? (
                  <img
                    src={
                      event.package.image_base64.startsWith("data:")
                        ? event.package.image_base64
                        : `data:image/webp;base64,${event.package.image_base64}`
                    }
                    alt={event.package?.name || "Paquete"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      color: "text.secondary",
                      p: 2,
                    }}
                  >
                    <Typography variant="body2">Sin imagen</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: "0.8rem",
                        color: getPackageColor(event.child_gender),
                        fontWeight: "bold"
                      }}
                    >
                      {event.package?.name || "Paquete"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Divider sx={{ my: 2 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ fontStyle: "italic", fontSize: "0.85rem" }}
        >
          <strong>NOTA IMPORTANTE:</strong> En caso de cancelación del evento,
          no se reembolsa el anticipo pagado. Este contrato es válido únicamente
          para la fecha y horario especificados.
        </Typography>
      </Paper>

      {/* Segunda página del contrato - Detalles del evento */}
      <Paper
        ref={contractPage2Ref}
        sx={{
          p: 4,
          maxWidth: "210mm",
          minHeight: "297mm",
          mx: "auto",
          backgroundColor: "white",
          mt: 3,
          "@media print": {
            pageBreakBefore: "always",
            boxShadow: "none",
            margin: 0,
            padding: "20mm",
          },
        }}
      >
        {/* Header de la segunda página */}
        <Box display="flex" alignItems="center" mb={4}>
          <img
            src={logoImage}
            alt="Divertilandia Logo"
            style={{ width: "80px", height: "80px", objectFit: "contain" }}
          />
          <Box sx={{ flex: 1, textAlign: "center", ml: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 1,
              }}
            >
              Divertilandia ENLU
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "text.secondary" }}
            >
              DETALLES DEL EVENTO
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fecha: {new Date().toLocaleDateString()} | Contrato No. {event.id}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Desglose de costos */}
        <Card sx={{ mb: 3, backgroundColor: "#f5f5f5" }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 2,
              }}
            >
              DESGLOSE DE COSTOS
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ color: getPackageColor(event.child_gender), fontWeight: "bold" }}>
                      Paquete Base:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(packagePrice)}
                    </TableCell>
                  </TableRow>
                  {Number(event.discount_percentage || 0) > 0 && (
                    <TableRow>
                      <TableCell>
                        Descuento ({event.discount_percentage}%):
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", color: "red" }}
                      >
                        -{formatCurrency(discountAmount)}
                      </TableCell>
                    </TableRow>
                  )}
                  {extraHoursCost > 0 && (
                    <TableRow>
                      <TableCell>
                        Horas Extras ({event.extra_hours_count}):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(extraHoursCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  {extraTablesCost > 0 && (
                    <TableRow>
                      <TableCell>
                        Mesas Extras ({event.extra_tables}):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(extraTablesCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  {extraChairsCost > 0 && (
                    <TableRow>
                      <TableCell>
                        Sillas Extras ({event.extra_chairs}):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(extraChairsCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  {extraPlaypensCost > 0 && (
                    <TableRow>
                      <TableCell>
                        Corralitos ({event.extra_playpens}):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(extraPlaypensCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  {extraToysCost > 0 && (
                    <TableRow>
                      <TableCell>
                        Juguetes Extras ({event.extra_toys}):
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(extraToysCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  {extraServicesCost > 0 && (
                    <TableRow>
                      <TableCell>
                        Servicios Adicionales{event.extra_services ? ` (${event.extra_services})` : ''}:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatCurrency(extraServicesCost)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ borderTop: "2px solid #ccc" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        color: divertilandiaColors.standard.primary,
                      }}
                    >
                      TOTAL:
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        color: divertilandiaColors.standard.primary,
                      }}
                    >
                      {formatCurrency(totalCost)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Anticipo Pagado:</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(advancePayment)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ borderTop: "1px solid #ccc" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        color: divertilandiaColors.standard.primary,
                      }}
                    >
                      SALDO PENDIENTE:
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        color: divertilandiaColors.standard.primary,
                      }}
                    >
                      {formatCurrency(remainingBalance)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Detalles del evento */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 2,
              }}
            >
              INFORMACIÓN DEL EVENTO
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                      Fecha del Evento:
                    </TableCell>
                    <TableCell>{formatDate(event.event_date)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Hora de Inicio:
                    </TableCell>
                    <TableCell>{formatTime(event.start_time)}</TableCell>
                  </TableRow>
                  {event.end_time && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Hora de Fin:
                      </TableCell>
                      <TableCell>{formatTime(event.end_time)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Dirección:
                    </TableCell>
                    <TableCell>{event.address || "Por definir"}</TableCell>
                  </TableRow>
                  {event.extra_hours_count > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Horas Extras:
                      </TableCell>
                      <TableCell>{event.extra_hours_count} horas</TableCell>
                    </TableRow>
                  )}
                  {event.extra_tables > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Mesas Extras:
                      </TableCell>
                      <TableCell>{event.extra_tables}</TableCell>
                    </TableRow>
                  )}
                  {event.extra_chairs > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Sillas Extras:
                      </TableCell>
                      <TableCell>{event.extra_chairs}</TableCell>
                    </TableRow>
                  )}
                  {event.extra_playpens > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Corralitos:
                      </TableCell>
                      <TableCell>{event.extra_playpens}</TableCell>
                    </TableRow>
                  )}
                  {event.extra_toys > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Juguetes Extras:
                      </TableCell>
                      <TableCell>{event.extra_toys}</TableCell>
                    </TableRow>
                  )}
                  {event.extra_services && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Servicios Adicionales:
                      </TableCell>
                      <TableCell>{event.extra_services}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Firmas */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 3,
              }}
            >
              FIRMAS DE CONFORMIDAD
            </Typography>
            <Box display="flex" justifyContent="space-between" mt={4}>
              <Box textAlign="center" sx={{ width: "45%" }}>
                <Box
                  sx={{ borderBottom: "1px solid #000", mb: 1, height: "60px" }}
                />
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Divertilandia ENLU
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Prestador del Servicio
                </Typography>
              </Box>
              <Box textAlign="center" sx={{ width: "45%" }}>
                <Box
                  sx={{ borderBottom: "1px solid #000", mb: 1, height: "60px" }}
                />
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {event.contact_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cliente / Contratante
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ fontStyle: "italic", fontSize: "0.85rem" }}
        >
          Este contrato es válido únicamente para la fecha y horario
          especificados. Cualquier modificación deberá ser acordada por escrito
          entre ambas partes.
        </Typography>
      </Paper>

      {/* Tercera página del contrato - Cláusulas */}
      <Paper
        ref={contractPage3Ref}
        sx={{
          p: 4,
          maxWidth: "210mm",
          minHeight: "297mm",
          mx: "auto",
          backgroundColor: "white",
          mt: 3,
          "@media print": {
            pageBreakBefore: "always",
            boxShadow: "none",
            margin: 0,
            padding: "20mm",
          },
        }}
      >
        {/* Header de la tercera página */}
        <Box display="flex" alignItems="center" mb={4}>
          <img
            src={logoImage}
            alt="Divertilandia Logo"
            style={{ width: "80px", height: "80px", objectFit: "contain" }}
          />
          <Box sx={{ flex: 1, textAlign: "center", ml: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 1,
              }}
            >
              Divertilandia ENLU
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "text.secondary" }}
            >
              CLÁUSULAS DE CONTRATO
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contrato No. {event.id}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Cláusulas del contrato */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 3,
              }}
            >
              CLÁUSULAS DE CONTRATO: Contrato # {event.id}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>1.</strong> La renta es por el horario y fecha
                especificada. El anticipo no es rembolsable en caso de
                cancelación.
              </Typography>

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>2.</strong> El precio establecido se pagará en cuanto se
                deje instalado y funcionando los juegos.
              </Typography>

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>3.</strong> El prestador de servicio "DIVERTILANDIA
                ENLU" No se responsabiliza por eventuales daños que se puedan
                ocasionar en el juego y/o sus accesorios, ni de lesiones en las
                personas por el mal uso, falta de precaución y/o no acatar las
                instrucciones y restricciones del juego mencionadas en las
                cláusulas 4 y 5 del presente contrato.
              </Typography>

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>4.</strong> No deberá excederse del límite de edad y/o
                peso de las personas que hagan uso del juego que es de 0 a 5
                años, y hasta 54 kg totales.
              </Typography>

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>5.</strong> No se deberá hacer uso del juego con ningún
                tipo de calzado, juguete, alimento, bebida, anteojos, lentes,
                animales, espuma, líquido, serpentina, instrumento punzocortante
                y/o cualquier otro objeto que ponga en riesgo la integridad
                física de las personas y el buen funcionamiento y/o durabilidad
                del juego y sus accesorios. Así mismo no se debe fumar en el
                juego ni en las inmediaciones del mismo.
              </Typography>

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>6.</strong> Todo daño al juego y/o sus accesorios y
                componentes o la falta de algún material de los mismos que se
                haya entregado conforme al presente contrato será pagado en su
                totalidad por el contratante:
              </Typography>

              {/* Cláusulas dinámicas basadas en los juguetes del paquete */}
              {getPackageClauses().length > 0 && (
                <Box sx={{ ml: 2, mb: 2 }}>
                  {getPackageClauses().map((clause, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      paragraph
                      sx={{
                        textAlign: "justify",
                        lineHeight: 1.6,
                        mb: 1,
                        fontWeight: "bold",
                      }}
                    >
                      • {clause}
                    </Typography>
                  ))}
                </Box>
              )}

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>7.</strong> Ante amenaza o presencia de lluvia, fuerte
                viento y/o tormenta se deberá retirar a resguardo, y el juego
                inflable y sus accesorios deberán ser cubiertos. En este caso no
                se reembolsará ninguna cantidad de dinero ya pagado por la
                renta.
              </Typography>

              <Typography
                variant="body2"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.6 }}
              >
                <strong>8.</strong> El prestador de servicio "DIVERTILANDIA
                ENLU" Se reserva el derecho de no rentar el juego si considera
                que la superficie y/o (en su caso) la corriente eléctrica pone
                en riesgo el buen funcionamiento y/o durabilidad del juego. Lo
                mismo aplicará en caso de presentarse condiciones climáticas
                adversas cuando se trate de un evento al aire libre.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{ fontStyle: "italic", display: "block" }}
        >
          Divertilandia ENLU - Contrato No. {event.id} -{" "}
          {new Date().toLocaleDateString()}
        </Typography>
      </Paper>

      {/* Cuarta página del contrato - Cláusulas Adicionales */}
      <Paper
        ref={contractPage4Ref}
        sx={{
          p: 2,
          maxWidth: "210mm",
          minHeight: "297mm",
          mx: "auto",
          backgroundColor: "white",
          mt: 3,
          "@media print": {
            pageBreakBefore: "always",
            boxShadow: "none",
            margin: 0,
            padding: "15mm",
          },
        }}
      >
        {/* Header de la cuarta página */}
        <Box display="flex" alignItems="center" mb={1.5}>
          <img
            src={logoImage}
            alt="Divertilandia Logo"
            style={{ width: "60px", height: "60px", objectFit: "contain" }}
          />
          <Box sx={{ flex: 1, textAlign: "center", ml: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 0.5,
              }}
            >
              Divertilandia ENLU
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", color: "text.secondary", mb: 0 }}
            >
              CLÁUSULAS ADICIONALES
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Contrato No. {event.id}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Cláusulas adicionales de responsabilidad */}
        <Card sx={{ mb: 1.5, backgroundColor: "#f9f9f9" }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                color: divertilandiaColors.standard.primary,
                mb: 1.5,
              }}
            >
              Cláusulas Adicionales de Responsabilidad para el Cuidado de Niños:
            </Typography>

            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.4, mb: 1 }}
              >
                <strong>9. Responsabilidad del Cuidado:</strong>
                <br />
                El servicio de cuidado de niños proporcionado por "Divertilandia
                Enlu" es un apoyo adicional para brindar mayor tranquilidad a
                los padres, pero no se puede garantizar la seguridad absoluta de
                los menores. Los cuidadores estarán pendientes en todo momento
                en el área de juegos, ayudando a los niños a subir a las
                atracciones, tratando de captar su atención para que se
                mantengan en la zona de juegos, especialmente a los más
                pequeños. También ayudarán a los niños a subir a los inflables,
                levantarlos en caso de caídas, e informar rápidamente a los
                padres en caso de un incidente de mayor gravedad o si un niño
                llora y necesita recurrir a su padre o responsable. Todo esto se
                realizará únicamente dentro del área de juegos de "Divertilandia
                Enlu". Los cuidadores no se hacen responsables por accidentes,
                golpes, ni cualquier posible afectación a los niños bajo su
                cuidado. Es responsabilidad de los padres supervisar a sus hijos
                en todo momento.
              </Typography>

              <Typography
                variant="caption"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.4, mb: 1 }}
              >
                <strong>10. Actividades No Cubiertas:</strong>
                <br />
                Los cuidadores no son responsables de cambiar ropa, cambiar
                pañales, acompañar al baño, ni realizar ninguna otra actividad
                de cuidado personal de los niños. En caso de que algún niño se
                pierda de vista, "Divertilandia Enlu" y los cuidadores quedan
                completamente exentos de responsabilidad. La vigilancia y
                ubicación de los niños es responsabilidad exclusiva de los
                padres. Los cuidadores no ayudarán a meseros, ni participarán en
                la hora de romper piñatas, ni llevarán a los niños al lugar
                donde se encuentren sus padres.
              </Typography>

              <Typography
                variant="caption"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.4, mb: 1 }}
              >
                <strong>11. Atracciones Acuáticas:</strong>
                <br />
                Los cuidadores no pueden ingresar al agua, alberca, río, pila,
                ni a cualquier otra atracción acuática. No se asume
                responsabilidad alguna en caso de accidentes o incidentes
                relacionados con atracciones acuáticas. La única área de
                visualización y supervisión por parte de los cuidadores es la
                zona de juegos de "Divertilandia Enlu".
              </Typography>

              <Typography
                variant="caption"
                paragraph
                sx={{ textAlign: "justify", lineHeight: 1.4, mb: 0.5 }}
              >
                <strong>12. Exclusión de Responsabilidad:</strong>
                <br />
                "Divertilandia Enlu" no se hace responsable por daños o lesiones
                causadas por el uso inapropiado de los juegos o por no seguir
                las instrucciones y restricciones establecidas en este contrato.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Firma de aceptación de cláusulas adicionales */}
        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography
              variant="caption"
              sx={{ 
                textAlign: "center", 
                fontWeight: "bold", 
                mb: 1, 
                mt: 0,
                display: "block",
                fontSize: "0.7rem",
                lineHeight: 1.3
              }}
            >
              Habiendo leído y aceptado las cláusulas adicionales de responsabilidad para el cuidado de niños, se firma el presente anexo al contrato
            </Typography>

            <Box display="flex" justifyContent="space-between" mt={1}>
              <Box textAlign="center" sx={{ width: "45%" }}>
                <Box
                  sx={{ borderBottom: "1px solid #000", mb: 0.5, height: "40px" }}
                />
                <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                  Divertilandia ENLU
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", display: "block" }}>
                  Prestador del Servicio
                </Typography>
              </Box>
              <Box textAlign="center" sx={{ width: "45%" }}>
                <Box
                  sx={{ borderBottom: "1px solid #000", mb: 0.5, height: "40px" }}
                />
                <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                  {event.contact_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", display: "block" }}>
                  Cliente / Contratante
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 0.5 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{ fontStyle: "italic", display: "block", fontSize: "0.65rem" }}
        >
          Divertilandia ENLU - Anexo de Cláusulas Adicionales - Contrato No. {event.id} - {new Date().toLocaleDateString()}
        </Typography>
      </Paper>

      {/* Modal de loading para generación de PDF */}
      <Dialog
        open={generatingPdf}
        disableEscapeKeyDown
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            padding: 2,
            minWidth: 300,
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={3}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{ color: divertilandiaColors.standard.primary }}
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                Generando Contrato PDF
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por favor espera mientras se procesa el documento...
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {window.innerWidth < 768 
                  ? "En móvil esto puede tomar hasta 30 segundos" 
                  : "Esto puede tomar unos segundos"}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContractPDF;
